/**
 * Status labels and their brand tones — the one place UI copy for a status is
 * decided.
 *
 * `BRAND.md` §5 fixes a status vocabulary (Draft · Pending review · Posted ·
 * Reversed · Submitted to SARS · Period closed · Overdue) and says never to
 * invent a synonym. EQ Practice's enums are not on that list, and §11 of
 * `EQ_Practice_Scope_Rev2.md` — **decided by Tia, 2026-08-30** — resolves that
 * the other way round: these are the practice's real working words, simplified
 * with her on 2026-08-26 to match how the work actually flows, so `BRAND.md`
 * gets *extended* to cover them rather than these being force-fitted into words
 * that don't quite mean the same thing.
 *
 * So the labels below are the enum's own words in sentence case, carrying
 * BRAND's semantic tones. That edit to the suite-level BRAND doc is a Wimpie
 * call and is still outstanding; **this file is the exact list it needs**:
 *
 *   In progress · For review · Completed          (ObligationPeriod)
 *   Not started · Sent to client · Awaiting signature ·
 *   Signed · On hold · Not applicable             (Job, additionally)
 *
 * `Overdue` is already in BRAND's list and keeps its Danger tone unchanged.
 */

export type Tone = 'neutral' | 'warning' | 'success' | 'danger' | 'info'

export interface StatusStyle {
  label: string
  tone: Tone
}

const STATUS_STYLES: Readonly<Record<string, StatusStyle>> = {
  // ObligationPeriod — in_progress -> for_review -> completed.
  in_progress: { label: 'In progress', tone: 'neutral' },
  for_review: { label: 'For review', tone: 'warning' },
  completed: { label: 'Completed', tone: 'success' },

  // Job keeps the fuller original pipeline (§11).
  not_started: { label: 'Not started', tone: 'neutral' },
  sent_to_client: { label: 'Sent to client', tone: 'info' },
  awaiting_signature: { label: 'Awaiting signature', tone: 'warning' },
  signed: { label: 'Signed', tone: 'info' },
  on_hold: { label: 'On hold', tone: 'neutral' },
  not_applicable: { label: 'Not applicable', tone: 'neutral' },
}

export function statusStyle(status: string): StatusStyle {
  // An unmapped value is shown as-is rather than hidden: a status this file
  // doesn't know about is a real thing that happened in the database, and
  // silently rendering nothing would be worse than rendering the raw word.
  return STATUS_STYLES[status] ?? { label: status, tone: 'neutral' }
}

/**
 * What a status means and what happens next, for the hover help on a status
 * badge. Periods and jobs have different pipelines (see
 * `server/src/status-transitions.ts`), so the same word can need different
 * words of help. Facts only from that file and the role rules in CLAUDE.md:
 * a manager marks a period completed; completed is one-way for both;
 * a period's not applicable can be reopened, a job's cannot.
 */
const PERIOD_HELP: Readonly<Record<string, string>> = {
  in_progress: 'Open. Move it to For review once your part of the work is done.',
  for_review:
    'Waiting for a manager to check it. A manager marks it Completed, or sends it back to In progress.',
  completed: 'Done. Completed is final and cannot be reopened.',
  not_applicable: 'Nothing was due for this period; the reason is recorded. A manager can reopen it.',
}

const JOB_HELP: Readonly<Record<string, string>> = {
  not_started: 'Not started yet.',
  in_progress: 'Being worked on.',
  sent_to_client: 'Sent to the client; waiting on them.',
  awaiting_signature: "Waiting for the client's signature.",
  signed: 'Signed by the client.',
  on_hold: 'Paused. It resumes to In progress.',
  not_applicable: 'Not needed. For a job this is final.',
  completed: 'Done. Completed is final and cannot be reopened.',
}

export function statusHelp(status: string, kind: 'obligation' | 'job'): string | null {
  return (kind === 'obligation' ? PERIOD_HELP : JOB_HELP)[status] ?? null
}

/** Tailwind classes per tone, from BRAND.md §2's semantic palette. */
export const TONE_CLASSES: Readonly<Record<Tone, string>> = {
  neutral: 'bg-eq-neutral-bg text-eq-neutral',
  warning: 'bg-eq-warning-bg text-eq-warning',
  success: 'bg-eq-success-bg text-eq-success',
  danger: 'bg-eq-danger-bg text-eq-danger',
  info: 'bg-eq-ice text-eq-deep',
}

/**
 * "14 days overdue" / "due in 16 days" / "due today".
 *
 * BRAND.md §7: name the form, give the date, state the consequence. The
 * singular is spelled out rather than pluralised with "(s)".
 */
export function dueLabel(daysOverdue: number): string {
  if (daysOverdue > 0) {
    return `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue`
  }
  if (daysOverdue === 0) return 'due today'
  const days = -daysOverdue
  return `due in ${days} day${days === 1 ? '' : 's'}`
}
